import { Badge } from '../Badge';
import type { Expense } from '@/api/client';

interface ExpenseTableProps {
  expenses: Expense[];
  onExpenseClick: (expense: Expense) => void;
}

export function ExpenseTable({ expenses, onExpenseClick }: ExpenseTableProps) {
  const formatCurrency = (amount: number) =>
    `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysUntilDeletion = (deletionScheduledAt?: string): number | null => {
    if (!deletionScheduledAt) return null;
    const now = new Date();
    const del = new Date(deletionScheduledAt);
    const diff = Math.ceil((del.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden">
      <table className="w-full">
        <thead className="bg-[#F7FAFC] border-b border-[#E2E8F0]">
          <tr>
            <th className="text-left px-6 py-3 text-[12px] font-semibold text-[#718096]">Date</th>
            <th className="text-left px-6 py-3 text-[12px] font-semibold text-[#718096]">Provider</th>
            <th className="text-left px-6 py-3 text-[12px] font-semibold text-[#718096]">Description</th>
            <th className="text-right px-6 py-3 text-[12px] font-semibold text-[#718096]">Amount</th>
            <th className="text-left px-6 py-3 text-[12px] font-semibold text-[#718096]">Status</th>
            <th className="text-center px-6 py-3 text-[12px] font-semibold text-[#718096]">Docs</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E2E8F0]">
          {expenses.map((expense) => {
            const daysLeft = getDaysUntilDeletion(expense.deletionScheduledAt);
            const lockTitle = daysLeft !== null
              ? `Over free limit — deleted in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} unless you upgrade`
              : 'Over free limit';

            return (
              <tr
                key={expense.id}
                className={`cursor-pointer ${expense.isOverFreeLimit ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-[#F7FAFC]'}`}
                onClick={() => onExpenseClick(expense)}
              >
                <td className="px-6 py-4 text-[14px] text-[#4A5568]">
                  {formatDate(expense.serviceDate)}
                </td>
                <td className="px-6 py-4 text-[14px] font-medium text-[#1B365D]">
                  <div className="flex items-center gap-1.5">
                    {expense.isOverFreeLimit && (
                      <span title={lockTitle}>
                        <svg className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </span>
                    )}
                    {expense.provider}
                  </div>
                </td>
                <td className="px-6 py-4 text-[14px] text-[#4A5568] max-w-[200px] truncate">
                  {expense.description}
                </td>
                <td className="px-6 py-4 text-[14px] font-semibold text-[#1B365D] text-right font-mono">
                  {formatCurrency(expense.amount)}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1.5">
                    {expense.isDraft && (
                      <Badge variant="gold" size="default">Draft</Badge>
                    )}
                    {expense.isOverFreeLimit ? (
                      <Badge variant="gold" size="default">
                        {daysLeft !== null ? `${daysLeft}d left` : 'Over limit'}
                      </Badge>
                    ) : (
                      <Badge
                        variant={expense.status === 'FullyReimbursed' ? 'green' : expense.status === 'PartiallyReimbursed' ? 'gold' : 'gray'}
                        size="default"
                      >
                        {expense.status === 'FullyReimbursed' ? 'Reimbursed' : expense.status === 'PartiallyReimbursed' ? 'Partial' : 'Unreimbursed'}
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  {expense.documentCompleteness.status === 'Complete' ? (
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#10B981]" title="Documentation complete" />
                  ) : expense.documentCompleteness.status === 'Incomplete' ? (
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#C9A227]" title={
                      [
                        !expense.documentCompleteness.hasReceipt && 'Missing: Receipt',
                        expense.documentCompleteness.requiresLMN && !expense.documentCompleteness.hasLMN && 'Missing: Letter of Medical Necessity',
                      ].filter(Boolean).join(', ')
                    } />
                  ) : (
                    <span className="text-[#E2E8F0]">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
